<template>
    <div class="tournament-awards-card">
        <h2 v-if="showAwards" class="title">{{ $t('awards_TournamentAwardsCard.title', { total: formattedTotalAwardValue }) }}</h2>
        <span v-if="showAwards" class="disclaimer">({{ $t('awards_TournamentAwardsCard.disclaimer') }})</span>
        <div v-if="showAwards" class="carousel">
            <div class="carousel-inner" :style="carouselStyle">
                <div class="carousel-item" v-for="(award) in awards" :key="award.rank">
                    <img
                        v-if="getMedalSrc(award.rank)"
                        :src="getMedalSrc(award.rank)"
                        class="award-medal-img"
                        :class="getMedalClass(award.rank)"
                        :alt="`Medal ${award.rank}`"
                    />
                    <img v-else src="@/assets/tournament-facelift/medal-prize.svg" class="award-medal-img"
                        :class="getMedalClass(award.rank)" :alt="`Medal ${award.rank}`" />
                    <!-- SFB-1298: no hard character cut; the name and description wrap
                         to two lines and clamp with an ellipsis, with the full text on hover. -->
                    <b :class="getMedalClass(award.rank)" class="award-name" :title="award.title">{{ award.title }}</b>
                    <span class="award-description" :title="award.description">{{ award.description }}</span>
                    <b class="award-rank">{{ getPlaceText(award.rank) }}</b>
                </div>
            </div>
        </div>
        <div v-if="false" class="actions">
            <button v-if="faqsUrl" class="faqs-btn" @click="goToFAQs">{{ $t('viewFAQs') }}</button>
            <button v-if="false" class="rules-btn" @click="goToRules">{{ $t('viewRules') }}</button>
        </div>
        <hr v-if="showAwards && showSponsor && ((partnerLogo && partnerLogo.length) || partnerLogoPreview)" />
        <div class="partner" v-if="showSponsor && ((partnerLogo && partnerLogo.length) || partnerLogoPreview)">
            <b class="thanks-to-txt">{{ $t('thanksTo') }}</b>
            <!-- <img :src="partnerLogo" alt="partner-logo" /> -->
            <div class="partner-logo-wrap">
                <div v-if="partnerLogoPreview === null" class="partner-logo-loading">
                 <!-- opcional: skeleton / loading -->
                </div>

                <img
                     v-else-if="partnerLogoPreview"
                    :src="partnerLogoPreview"
                    alt="partner-logo"
                    class="partner-logo"
                />
            </div>

        </div>
    </div>
</template>

<script>
    import Repository from "@/repositories/tournament"; 

export default {
    name: 'TournamentAwardsCard',
    props: {
        showAwards: { type: Boolean, default: true },
        showSponsor: { type: Boolean, default: true },
        awards: {
            type: Array,
            required: true,
            default: () => [
                { rank: 1, name: 'Default Award 1', value: 100 },
                { rank: 2, name: 'Default Award 2', value: 200 },
                { rank: 3, name: 'Default Award 3', value: 300 },
            ],
        },
        faqsUrl: {
            type: String,
            required: false,
        },
        rulesUrl: {
            type: String,
            required: false,
        },
        partnerLogo: {
            type: String,
            required: false,
            default: 'https://hub.swiset.com/wp-content/uploads/2024/01/Swiset-Logo-SVG-for-Light.png',
        },
    },
    data() {
        return {
            currentIndex: 0,
                partnerLogoPreview: null, 
                isInitializing: false,
        };
    },
    computed: {
        totalAwardValue() {
            return this.awards.reduce((total, award) => {
                if (award.estimatedValue) {
                    return Number(total) + Number(award.estimatedValue);
                }
            }, 0);
        },
        formattedTotalAwardValue() {
            return this.totalAwardValue ? this.totalAwardValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : Number(0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        },
        carouselStyle() {
            return {
                transform: `translateX(-${this.currentIndex * 100}%)`,
                transition: 'transform 0.5s ease-in-out',
            };
        },
        shouldShowSponsor() {
            const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
            if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
                return true;
            }
            return selectedFeatures.includes('TOURNAMENT_RANKING_SPONSORED');
        },

        medalTypeFromState() {
            return this.$store.state.tournament?.tournamentSettings.medalType || "default";
        },
        normalizedMedalType() {
            const t = (this.medalTypeFromState || "default").toLowerCase();
            const allowed = ["default", "3d", "neon", "retro"];
            return allowed.includes(t) ? t : "default";
        },

    },
    methods: {
        async initPartnerLogo() {
            if (!this.showSponsor || !this.shouldShowSponsor) {
            this.partnerLogoPreview = null;
            return;
        }
        this.isInitializing = true;
        this.partnerLogoPreview = null;
        const tournamentId = this.$store.state.tournament?.tournamentId;
        const isDark = !!this.$vuetify?.theme?.dark;

        try {
            if (tournamentId) {
                const res = await Repository.getTournamentBranding(tournamentId);
                const branding = res?.data || {};
                const newUrl = isDark
                    ? branding.dark?.fullLogo
                    : branding.light?.fullLogo;

                if (newUrl && typeof newUrl === "string" && newUrl.length > 0) {
                    this.partnerLogoPreview = newUrl;
                    return;
                }
            }
        }   catch (e) {
             console.warn("Branding new endpoint failed, falling back:", e);
                }

      try {
        if (!this.partnerLogo) {
          this.partnerLogoPreview = null;
          return;
        }
    
        const r = await Repository.getTournamentBrandingImage(this.partnerLogo);
        if (r?.status < 400 && r?.data) {
          this.partnerLogoPreview = r.data;
        } else {

          this.partnerLogoPreview = this.partnerLogo;
        }
      } catch (e) {
        console.error("Error fetching partner logo fallback:", e);
        this.partnerLogoPreview = this.partnerLogo || null;
      } finally {
        this.isInitializing = false;
      }
    },
        getMedalSrc(rank) {
          if (![1, 2, 3].includes(rank)) return null;
        
          const folder = this.normalizedMedalType || "default";
            try {
            return require(`@/assets/icons/${folder}/Medal-${rank}.png`);
          } catch (e) {
            return null;
          }
        },
        startCarousel() {
            setInterval(() => {
                this.currentIndex = (this.currentIndex + 1) % this.awards.length;
            }, 5000);
        },
        normalizedUiLocale() {
            const raw = String(this.$i18n.locale || 'en').toLowerCase();
            if (raw.startsWith('zh-hant')) return 'zh-hant';
            return raw.split('-')[0];
        },
        englishOrdinal(rank) {
            const suffixes = ['th', 'st', 'nd', 'rd'];
            const v = rank % 100;
            const suf = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
            return `${rank}${suf}`;
        },
        getPlaceText(rank) {
            const loc = this.normalizedUiLocale();

            if (loc === 'th') {
                return this.$t('place_TournamentPositionCard', { rank });
            }

            if (loc === 'vi') {
                if (rank === 1) return this.$t('place_TournamentPositionCard_1');
                if (rank === 2) return this.$t('place_TournamentPositionCard_2');
                if (rank === 3) return this.$t('place_TournamentPositionCard_3');
                return this.$t('place_TournamentPositionCard_rank', { rank });
            }

            const rankOnlyPhraseLocales = ['ja', 'ko', 'zh', 'zh-hant', 'hi', 'id', 'ru'];
            if (rankOnlyPhraseLocales.includes(loc)) {
                return this.$t('place_TournamentPositionCard', { rank });
            }

            if (loc === 'en') {
                return `${this.englishOrdinal(rank)} ${this.$t('place_TournamentPositionCard')}`;
            }

            if (loc === 'es' || loc === 'pt') {
                return `${rank}º ${this.$t('place_TournamentPositionCard')}`;
            }

            return `${rank} ${this.$t('place_TournamentPositionCard')}`;
        },
        getMedalClass(rank) {
            if (rank === 1) return 'gold';
            if (rank === 2) return 'silver';
            if (rank === 3) return 'bronze';
            return '';
        },
        goToFAQs() {
            window.open(this.faqsUrl, 'self');
        },
        goToRules() {
            window.open(this.rulesUrl, 'self');
        },
        parseValueFromTitle(title) {
            const match = title.match(/\$([\d,.]+)/);
            if (match) {
                const value = match[1].replace(/[,.]/g, '');
                return parseInt(value, 10);
            }
            return null;
        },
        isDarkMode() {
            return !!this.$vuetify?.theme?.dark;
        },
    },
    mounted() {
        this.startCarousel();
        this.initPartnerLogo();

    },
    watch: {
      partnerLogo() {
        this.initPartnerLogo();
      },
      showSponsor() {
        this.initPartnerLogo();
      },
    "$vuetify.theme.dark"() {
        this.initPartnerLogo();
        },
    }


};
</script>


<style scoped lang="scss">
.partner-logo-wrap {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.partner-logo {
  max-width: 250px;
  max-height: 70px;
  object-fit: contain;
}

.partner-logo-loading {
  width: 250px;
  height: 70px;
  border-radius: 10px;
  background: rgba(0,0,0,0.06);
}

#broker-app {
    .tournament-awards-card h2 {
        font-family: 'Avenir', sans-serif !important;
        font-weight: bold;
        font-size: 24px !important;
    }
}

.tournament-awards-card {
    text-align: center;
    border: 1px solid #EBEBEB;
    border-radius: 15px;
    background-color: var(--v-fullWhite-base);
    font-family: 'Avenir', sans-serif;
    display: flex;
    flex-direction: column;
    padding: 25px 5px;
    justify-content: center;
    margin-left: auto;

    h2 {
        font-family: 'Aeonik', 'Avenir', sans-serif !important;
        font-weight: bold;
        font-size: 24px;
        line-height: 1.5;
    }

    .disclaimer {
        font-size: 14px;
        color: #9a9a9a;
        margin-bottom: 6px;
    }

    .actions {
        display: flex;
        justify-content: space-between;
        margin-top: 18px;

        .faqs-btn,
        .rules-btn {
            border: none;
            padding: 10px 30px;
            border-radius: 5px;
            font-size: 14px;
            cursor: pointer;
        }

        .faqs-btn {
            width: 100%;
            background-color: var(--v-progressBarBackground-base);
        }

        .rules-btn {
            background-color: var(--v-mainGreen-base);
            color: black;
        }
    }

    hr {
        border: 1px solid #9a9a9a;
        opacity: 0.6;
        margin-top: 32px;
        margin-bottom: 32px;
    }

    .partner {
        display: flex;
        flex-direction: column;
        align-items: center;

        .thanks-to-txt {
            font-size: 18px;
        }

        img {
            width: 250px;
            margin-top: 10px;
        }
    }
}

.carousel {
    position: relative;
    // SFB-1298: size to the tallest slide instead of a fixed 140px, so a
    // two-line award name or description is never cut off at the bottom.
    min-height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.carousel-inner {
    display: flex;
    align-items: stretch;
    width: 100%;
}

.carousel-item {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    text-align: center;
    padding: 0 10px;

    .award-medal-img {
        width: 52px;
        height: 52px;
        object-fit: contain;
        display: block;
        margin-bottom: 5px;
    }


    i {
        font-size: 48px;
    }

    // Two-line clamp: wraps long titles instead of cutting them after a few
    // characters; anything beyond two lines gets an ellipsis (full text in title).
    .award-name,
    .award-description {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
        max-width: 100%;
    }

    .award-name {
        font-family: 'Aeonik', 'Avenir', sans-serif;
        font-size: 22px;
        line-height: 1.2;
    }

    .award-description {
        font-size: 14px;
        line-height: 1.3;
        color: #9a9a9a;
        margin-top: 2px;
        padding-bottom: 6px;
    }

    .award-rank {
        font-family: 'Aeonik', 'Avenir', sans-serif;
        line-height: 1.5;
        font-size: 20px;
        margin-top: 0px;
        color: #9a9a9a;
    }
}

.gold {
    color: #FFB801;
}

.silver {
    color: silver;
}

.bronze {
    color: #cd7f32;
}
</style>